import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function AboutPage() {
  useEffect(() => {
    document.title = 'About Henry Adebisi | Finance & Strategy Leader';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Chartered Accountant and Finance Leader with over a decade of experience leading digital transformation, fintech innovation, and cross-border growth across Africa and the UK. Dedicated to building systems that enable sustainable business and financial inclusion.');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://henryadebisi.com'}/about`);
    }
    
    const keywords = document.querySelector('meta[name="keywords"]');
    if (keywords) {
      keywords.setAttribute('content', 'chartered accountant, finance leader, digital transformation, fintech innovation, cross-border growth');
    }

    // Add Schema.org JSON-LD
    const existingSchema = document.querySelector('script[type="application/ld+json"]');
    if (existingSchema) {
      existingSchema.remove();
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "mainEntity": {
        "@type": "Person",
        "name": "Henry Olamide Adebisi",
        "jobTitle": "Finance & Strategy Leader",
        "description": "Chartered Accountant and Finance Leader with over a decade of experience leading digital transformation, fintech innovation, and cross-border growth across Africa and the UK.",
        "url": import.meta.env.VITE_SITE_URL || 'https://henryadebisi.com',
        "hasCredential": [
          {
            "@type": "EducationalOccupationalCredential",
            "name": "Chartered Accountant (ACA)",
            "credentialCategory": "Professional Certification",
            "recognizedBy": {
              "@type": "Organization",
              "name": "Institute of Chartered Accountants of Nigeria (ICAN)"
            }
          }
        ],
        "knowsAbout": [
          "Strategic Finance",
          "Digital Transformation",
          "Fintech Innovation",
          "Cross-border Growth",
          "Financial Inclusion"
        ]
      }
    };

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = JSON.stringify(schema);
    document.head.appendChild(schemaScript);

    return () => {
      const schemaToRemove = document.querySelector('script[type="application/ld+json"]');
      if (schemaToRemove) {
        schemaToRemove.remove();
      }
    };
  }, []);

  const values = [
    {
      title: 'Integrity',
      description: 'Building trust through transparent financial practices and ethical leadership',
      icon: 'ri-shield-check-line'
    },
    {
      title: 'Strategy',
      description: 'Developing comprehensive frameworks for sustainable business growth',
      icon: 'ri-lightbulb-line'
    },
    {
      title: 'Innovation',
      description: 'Pioneering fintech solutions that transform African financial landscapes',
      icon: 'ri-rocket-line'
    },
    {
      title: 'Growth',
      description: 'Enabling scalable systems that drive cross-border expansion',
      icon: 'ri-line-chart-line'
    }
  ];

  return (
    <div className="min-h-screen bg-off-white">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="font-sora font-bold text-4xl lg:text-5xl text-navy mb-6">
                  Strategic Finance Leadership for Africa's Future
                </h1>
                <div className="space-y-6 text-gray-700 leading-relaxed">
                  <p>
                    Henry's journey spans from leading finance roles at Meristem, RenMoney, and Opay to transforming 
                    operations at Jobberman Nigeria and The African Talent Company (TATC) - Jobberman's Parent Company. His expertise bridges 
                    traditional finance with innovative fintech solutions across multiple African markets.
                  </p>
                  <p>
                    As a strategic finance leader, Henry has driven profitability turnarounds, managed large-scale 
                    donor partnerships with organizations like Mastercard Foundation, UNICEF, USAID, and UNDP, 
                    while building robust financial systems that enable sustainable growth across Nigeria, Ghana, Kenya, and Uganda.
                  </p>
                  <p>
                    His passion lies in creating financial frameworks and investment structures that empower SMEs 
                    and fintech companies to scale sustainably, bridging the gap between African businesses and 
                    global capital markets through innovative approaches to structured finance and strategic partnerships.
                  </p>
                </div>
                <div className="mt-8">
                  <Button variant="secondary" size="lg" href="/henry-adebisi-bio.pdf" className="inline-flex items-center">
                    <i className="ri-download-line mr-2"></i>
                    Download Bio
                  </Button>
                </div>
              </div>
              
              <div className="flex justify-center">
                <img 
                  src="https://static.readdy.ai/image/6b84a11e410079b56c6291a27cbf72c2/4aef92e2ccd2e54e1e74719d7b03332d.jpeg"
                  alt="Henry Olamide Adebisi - Professional Portrait"
                  className="rounded-lg shadow-2xl object-cover object-top w-full max-w-md"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="font-sora font-bold text-3xl lg:text-4xl text-navy mb-4">
                Core Values
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                The principles that guide Henry's approach to finance leadership and business transformation
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="w-12 h-12 bg-gold/10 rounded-lg flex items-center justify-center mb-6">
                    <i className={`${value.icon} text-2xl text-gold`}></i>
                  </div>
                  <h3 className="font-sora font-semibold text-xl text-navy mb-4">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-navy">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-sora font-bold text-3xl lg:text-4xl text-white mb-6">
              Ready to Transform Your Financial Strategy?
            </h2>
            <p className="text-gray-300 text-lg mb-8 leading-relaxed">
              Partner with Henry to build sustainable financial systems and unlock growth opportunities across African markets.
            </p>
            <Button variant="secondary" size="lg" href="/contact">
              Start a Conversation
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
